import { useMemo, useState } from "react";

const vlans = [
  { id: 100, name: "Visitantes", network: "172.16.100.0/24", gateway: "172.16.100.1", tone: "cyan" },
  { id: 200, name: "Corporativa", network: "172.16.200.0/24", gateway: "172.16.200.1", tone: "blue" },
  { id: 250, name: "CFTV", network: "172.16.250.0/24", gateway: "172.16.250.1", tone: "amber" },
  { id: 1000, name: "Gerência", network: "10.250.250.0/30", gateway: "10.250.250.1", tone: "violet" }
];

const switches = [
  { name: "SW01_GER", detail: "MikroTik CSS326-24G-2S+", role: "Gerenciável · distribuição", state: "Online", meta: "10.250.250.2/30" },
  { name: "SW02_CFTV", detail: "Switch PoE · 26 portas", role: "Câmeras e DVR", state: "16 câmeras", meta: "VLAN 250" },
  { name: "SW03_LOCAL_01", detail: "Switch Gigabit", role: "Rede local não gerenciável", state: "Ativo", meta: "VLAN 200" },
  { name: "SW04_LOCAL_02", detail: "Switch Gigabit", role: "Rede local não gerenciável", state: "Planejado", meta: "Uplink a confirmar" }
];

const directLinks = [
  { id: "LD-001", from: "SW01_GER · ether24", to: "SW02_CFTV · porta25", service: "Uplink CFTV · VLAN 250", status: "Link up" },
  { id: "LD-002", from: "SW02_CFTV · porta26", to: "DVR · ETH", service: "Gravação CFTV · VLAN 250", status: "Ativo" },
  { id: "LD-003 — LD-010", from: "Reserva técnica", to: "Ligação direta futura", service: "Fora dos 96 pontos", status: "Disponível" }
];

function tag(value, className = "") {
  return <span className={`infra-tag ${className}`}>{value}</span>;
}

export function InfrastructureTopologyPage() {
  const [activeView, setActiveView] = useState("connections");
  const [query, setQuery] = useState("");
  const cameras = useMemo(() => Array.from({ length: 16 }, (_, index) => {
    const number = index + 1;
    return {
      camera: `CAM-${String(number).padStart(2, "0")}`,
      switchPort: `SW02_CFTV · porta${String(number + 8).padStart(2, "0")}`,
      point: `PT_A_${String(number).padStart(3, "0")}`,
      internalPoint: `A-${String(number).padStart(3, "0")}`,
      vlan: "250 · CFTV"
    };
  }), []);
  const filteredCameras = cameras.filter((row) => Object.values(row).some((value) => value.toLowerCase().includes(query.toLowerCase())));

  return (
    <section className="infra-page">
      <header className="infra-hero">
        <div>
          <span className="infra-kicker">Infraestrutura do cliente</span>
          <h1>IVO BARBOZA ADVOGADOS</h1>
          <p>Topologia, VLANs, cabeamento estruturado e identificação técnica em uma única visão operacional.</p>
        </div>
        <div className="infra-hero-actions">
          {tag("Cadastro preparado", "success")}
          <button type="button" className="secondary">Exportar relatório</button>
        </div>
      </header>

      <div className="infra-stats">
        <article><span>Pontos de telecom</span><strong>96</strong><small>PT_A_001 a PT_A_096</small></article>
        <article><span>Câmeras IP</span><strong>16</strong><small>SW02 portas 09 a 24</small></article>
        <article><span>Switches</span><strong>4</strong><small>1 gerenciável · 1 PoE · 2 locais</small></article>
        <article><span>VLANs ativas</span><strong>4</strong><small>100 · 200 · 250 · 1000</small></article>
        <article><span>Links diretos</span><strong>10</strong><small>2 definidos · 8 reservas</small></article>
      </div>

      <div className="infra-overview-grid">
        <article className="infra-card infra-topology-card">
          <div className="infra-card-heading">
            <div><span className="infra-kicker">Diagrama lógico</span><h2>Visão geral da rede</h2></div>
            {tag("Atualizado hoje", "neutral")}
          </div>
          <div className="network-map">
            <div className="wan-row">
              <div className="network-node wan active"><b>UM TC</b><span>177.221.41.177/30</span></div>
              <div className="network-node wan standby"><b>VIVO</b><span>ether02 · desativado</span></div>
              <div className="network-node wan standby"><b>CLARO</b><span>ether03 · desativado</span></div>
            </div>
            <span className="network-arrow">↓</span>
            <div className="network-node router"><span>ROTEADOR DE BORDA</span><b>RTD_BORDA_IVB</b><small>RB3011 · ether10 trunk</small></div>
            <span className="network-arrow trunk">↓ <em>VLAN 100 · 200 · 250 · 1000</em></span>
            <div className="network-node core"><span>DISTRIBUIÇÃO</span><b>SW01_GER</b><small>CSS326 · 10.250.250.2/30</small></div>
            <div className="branch-lines" aria-hidden="true"><i></i><i></i><i></i><i></i></div>
            <div className="network-branches">
              <div className="network-node branch cftv"><b>SW02_CFTV</b><span>ether24 → porta25</span><small>PoE · VLAN 250</small></div>
              <div className="network-node branch"><b>SW03_LOCAL_01</b><span>ether09</span><small>Gigabit · VLAN 200</small></div>
              <div className="network-node branch pending"><b>SW04_LOCAL_02</b><span>uplink a confirmar</span><small>Gigabit · rede local</small></div>
              <div className="network-node branch wifi"><b>APs Wi-Fi</b><span>AP01 a AP05</span><small>VLANs 100 · 200 · 250</small></div>
            </div>
          </div>
        </article>

        <article className="infra-card">
          <div className="infra-card-heading"><div><span className="infra-kicker">Segmentação</span><h2>VLANs configuradas</h2></div></div>
          <div className="vlan-list">
            {vlans.map((vlan) => (
              <div className="vlan-row" key={vlan.id}>
                <span className={`vlan-id ${vlan.tone}`}>{vlan.id}</span>
                <div><b>{vlan.name}</b><small>{vlan.network}</small></div>
                <div className="vlan-gateway"><span>Gateway</span><b>{vlan.gateway}</b></div>
              </div>
            ))}
          </div>
          <div className="infra-note"><b>Telefonia IP</b><span>VLAN e configuração da Impacta 220R serão incluídas na próxima etapa.</span></div>
        </article>
      </div>

      <article className="infra-card equipment-card">
        <div className="infra-card-heading"><div><span className="infra-kicker">Inventário</span><h2>Equipamentos principais</h2></div><span className="infra-muted">4 switches cadastrados</span></div>
        <div className="equipment-grid">
          {switches.map((item) => (
            <div className="equipment-item" key={item.name}>
              <span className="equipment-icon">SW</span>
              <div><b>{item.name}</b><span>{item.detail}</span><small>{item.role}</small></div>
              <div className="equipment-state">{tag(item.state, item.state === "Planejado" ? "warning" : "success")}<small>{item.meta}</small></div>
            </div>
          ))}
        </div>
      </article>

      <div className="infra-tabs" role="tablist" aria-label="Detalhes de infraestrutura">
        <button className={activeView === "connections" ? "active" : ""} onClick={() => setActiveView("connections")}>DE-PARA das câmeras</button>
        <button className={activeView === "direct" ? "active" : ""} onClick={() => setActiveView("direct")}>Uplinks e ligações diretas</button>
        <button className={activeView === "labels" ? "active" : ""} onClick={() => setActiveView("labels")}>Modelo de etiquetas</button>
      </div>

      {activeView === "connections" ? (
        <article className="infra-card infra-table-card">
          <div className="infra-card-heading">
            <div><span className="infra-kicker">Rastreabilidade</span><h2>DE-PARA — 16 câmeras</h2></div>
            <input aria-label="Buscar câmera ou ponto" placeholder="Buscar câmera, porta ou ponto…" value={query} onChange={(event) => setQuery(event.target.value)} />
          </div>
          <div className="infra-table-wrap"><table className="infra-table"><thead><tr><th>Equipamento</th><th>Origem</th><th>Ponto identificado</th><th>ID interno</th><th>Rede</th><th>Status</th></tr></thead><tbody>
            {filteredCameras.map((row) => <tr key={row.camera}><td><b>{row.camera}</b></td><td>{row.switchPort}</td><td><code>{row.point}</code></td><td>{row.internalPoint}</td><td>{tag(row.vlan, "vlan")}</td><td>{tag("Preparado", "success")}</td></tr>)}
          </tbody></table></div>
          <footer className="infra-table-footer"><span>Mostrando {filteredCameras.length} de 16 conexões</span><span>Portas 09–24 reservadas para câmeras</span></footer>
        </article>
      ) : null}

      {activeView === "direct" ? (
        <article className="infra-card infra-table-card">
          <div className="infra-card-heading"><div><span className="infra-kicker">Fora dos 96 pontos</span><h2>Uplinks e ligações diretas</h2></div></div>
          <div className="infra-table-wrap"><table className="infra-table"><thead><tr><th>ID</th><th>Origem</th><th>Destino</th><th>Serviço</th><th>Status</th></tr></thead><tbody>
            {directLinks.map((link) => <tr key={link.id}><td><b>{link.id}</b></td><td>{link.from}</td><td>{link.to}</td><td>{link.service}</td><td>{tag(link.status, link.status === "Disponível" ? "neutral" : "success")}</td></tr>)}
          </tbody></table></div>
        </article>
      ) : null}

      {activeView === "labels" ? (
        <article className="infra-card label-workspace">
          <div><span className="infra-kicker">Brother PT-E310BT</span><h2>Prévia da etiqueta de identificação</h2><p>Impressão em duas linhas, sem os termos “DE” e “PARA”. O ponto de tomada (TO) fica reservado para uma etapa futura.</p><dl><div><dt>Linha superior</dt><dd>Switch e porta de origem</dd></div><div><dt>Linha inferior</dt><dd>Ponto de telecom (PT)</dd></div><div><dt>Série atual</dt><dd>PT_A_001 a PT_A_096</dd></div></dl></div>
          <div className="label-preview-wrap"><span>Exemplo para impressão</span><div className="label-tape"><b>SW02_CFTV_PT09</b><i></i><b>PT_A_001</b></div><small>Texto centralizado · duas linhas · identificação em ambas as extremidades</small></div>
        </article>
      ) : null}
    </section>
  );
}
